import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, Target, Gift, Users, Clock, MapPin, ListChecks, CheckCircle2, ArrowUpRight, Loader2 } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { SM_ACCENT } from "@/data/content";
import { SMLeadForm } from "@/components/SMLeadForm";

const Block = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-3xl border border-gray-100 p-6 sm:p-7 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
    <div className="flex items-center gap-2.5 mb-4">
      <span className="flex items-center justify-center w-9 h-9 rounded-xl" style={{ background: `${SM_ACCENT}14`, color: SM_ACCENT }}><Icon size={18} /></span>
      <h3 className="font-display font-700 text-base" style={{ color: "#3b0764" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const List = ({ items, ordered }) => (
  <ul className="space-y-2.5">
    {(items || []).map((t, i) => (
      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
        {ordered
          ? <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full text-[11px] font-700 flex items-center justify-center" style={{ background: `${SM_ACCENT}14`, color: SM_ACCENT }}>{i + 1}</span>
          : <CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: SM_ACCENT }} />}
        {t}
      </li>
    ))}
  </ul>
);

export default function SempliceMenteDetail() {
  const { slug } = useParams();
  const [c, setC] = React.useState(undefined);
  const [percorsi, setPercorsi] = React.useState([]);

  React.useEffect(() => {
    setC(undefined);
    api.get(`/sm/percorsi/${slug}`).then((r) => setC(r.data)).catch(() => setC(null));
    api.get("/sm/percorsi").then((r) => setPercorsi(r.data)).catch(() => {});
  }, [slug]);

  if (c === undefined) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin" style={{ color: SM_ACCENT }} size={30} /></div>;
  if (c === null) return <div className="max-w-3xl mx-auto px-4 py-32 text-center"><p className="text-gray-500">Percorso non trovato.</p><Link to="/semplicemente" className="text-[#8B5CF6] font-600">← Torna a SempliceMente</Link></div>;

  const Icon = Icons[c.icon] || Icons.Sparkles;
  const teacher = c.teacher;

  return (
    <div className="bg-white">
      <div className="relative h-64 sm:h-80 overflow-hidden">
        <img src={imgUrl(c.cover)} alt={c.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(59,7,100,0.25) 0%,rgba(59,7,100,0.75) 100%)" }} />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-8">
            <Link to="/semplicemente" data-testid="sm-back" className="inline-flex items-center gap-1.5 text-white/90 text-sm font-600 mb-4 hover:text-white transition-colors"><ArrowLeft size={16} /> SempliceMente</Link>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/95" style={{ color: SM_ACCENT }}><Icon size={24} /></span>
              <h1 className="font-display font-800 tracking-tight text-3xl sm:text-4xl text-white">{c.title}</h1>
              {c.format && <span className="text-xs font-600 px-3 py-1 rounded-full bg-white/20 text-white backdrop-blur">{c.format}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-lg text-gray-600 leading-relaxed max-w-3xl">{c.description}</motion.p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Block icon={Target} title="Obiettivi"><List items={c.obiettivi} /></Block>
          <Block icon={Gift} title="Benefici"><List items={c.benefici} /></Block>
        </div>

        {c.moduli && c.moduli.length > 0 && (
          <div className="mt-6">
            <Block icon={ListChecks} title="Programma del percorso"><List items={c.moduli} ordered /></Block>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InfoTile icon={Users} label="Destinatari" value={c.destinatari} />
          <InfoTile icon={Clock} label="Durata" value={c.durata} />
          <InfoTile icon={MapPin} label="Modalità" value={c.modalita} />
        </div>

        {teacher && (
          <Link to={`/docenti/${teacher.id}`} data-testid="sm-teacher-link" className="mt-6 flex items-center gap-4 bg-white rounded-3xl border border-gray-100 p-5 hover:shadow-md transition-shadow group">
            {teacher.photo
              ? <img src={imgUrl(teacher.photo)} alt={teacher.name} className="w-14 h-14 rounded-2xl object-cover" />
              : <span className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-700 text-lg" style={{ background: SM_ACCENT }}>{teacher.name?.[0]}</span>}
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-700 uppercase tracking-wide" style={{ color: SM_ACCENT }}>Docente del percorso</p>
              <p className="font-700 text-gray-900">{teacher.name}</p>
              <p className="text-sm text-gray-400 truncate">{teacher.role}</p>
            </div>
            <ArrowUpRight size={18} className="text-gray-300 group-hover:text-[#8B5CF6] transition-colors" />
          </Link>
        )}

        <div id="richiedi" className="mt-10 rounded-3xl p-8 sm:p-10 scroll-mt-24" style={{ background: "linear-gradient(135deg,#4c1d95 0%,#8B5CF6 100%)" }}>
          <div className="max-w-2xl mx-auto">
            <h3 className="font-display font-800 text-2xl sm:text-3xl text-white text-center">Interessato a "{c.title}"?</h3>
            <p className="mt-2 text-white/80 text-center">Lascia i tuoi dati: ti ricontatteremo per costruire il percorso su misura.</p>
            <div className="mt-7 bg-white rounded-3xl p-6 sm:p-7">
              <SMLeadForm percorsi={percorsi} defaultTopic={c.title} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const InfoTile = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-gray-400 mb-1.5"><Icon size={15} /><span className="text-xs font-600 uppercase tracking-wide">{label}</span></div>
    <p className="text-sm font-600 text-[#111827]">{value}</p>
  </div>
);
